import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "overlay",
    "spotlight",
    "card",
    "title",
    "body",
    "counter",
    "prevButton",
    "nextButton",
    "finishButton"
  ]

  static values = {
    steps: Array,
    planType: String,
    userId: String,
    userSignedIn: Boolean,
    userSeen: Boolean,
    visitorStorageKey: String,
    markSeenUrl: String
  }

  connect() {
    this.currentStepIndex = 0
    this.activeTarget = null
    this.rafId = null
    this.onResize = this.scheduleReposition.bind(this)
    this.onScroll = this.scheduleReposition.bind(this)

    if (!this.shouldShow()) return

    window.addEventListener("resize", this.onResize)
    window.addEventListener("scroll", this.onScroll, true)
    this.open()
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize)
    window.removeEventListener("scroll", this.onScroll, true)
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    this.cleanup()
  }

  next() {
    if (this.currentStepIndex >= this.steps.length - 1) {
      this.finish()
      return
    }

    this.currentStepIndex += 1
    this.renderStep()
  }

  prev() {
    if (this.currentStepIndex <= 0) return

    this.currentStepIndex -= 1
    this.renderStep()
  }

  close() {
    this.persistSeen()
    this.cleanup()
  }

  finish() {
    this.persistSeen()
    this.cleanup()
  }

  overlayClick(event) {
    if (event.target === this.overlayTarget) {
      this.finish()
    }
  }

  open() {
    this.overlayTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    this.renderStep()
  }

  renderStep() {
    const step = this.steps[this.currentStepIndex]
    if (!step) {
      this.finish()
      return
    }

    this.titleTarget.textContent = step.title || ""
    this.bodyTarget.textContent = step.body || ""
    this.counterTarget.textContent = `${this.currentStepIndex + 1}/${this.steps.length}`

    const target = this.resolveTarget(step.selector)
    this.activeTarget = target
    this.scrollTargetIntoView()
    this.scheduleReposition()

    const isFirst = this.currentStepIndex === 0
    const isLast = this.currentStepIndex === this.steps.length - 1
    this.prevButtonTarget.classList.toggle("invisible", isFirst)
    this.nextButtonTarget.classList.toggle("hidden", isLast)
    this.finishButtonTarget.classList.toggle("hidden", !isLast)
  }

  reposition() {
    if (!this.activeTarget || !this.isElementVisible(this.activeTarget)) {
      this.spotlightTarget.classList.add("hidden")
      this.cardTarget.style.left = "50%"
      this.cardTarget.style.top = "50%"
      this.cardTarget.style.bottom = "auto"
      this.cardTarget.style.transform = "translate(-50%, -50%)"
      return
    }

    const rect = this.activeTarget.getBoundingClientRect()
    const padding = 10
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const spotlightLeft = Math.max(rect.left - padding, 8)
    const spotlightTop = Math.max(rect.top - padding, 8)
    const spotlightWidth = Math.min(rect.width + padding * 2, viewportWidth - spotlightLeft - 8)
    const spotlightHeight = Math.min(rect.height + padding * 2, viewportHeight - spotlightTop - 8)

    this.spotlightTarget.classList.remove("hidden")
    this.spotlightTarget.style.left = `${spotlightLeft}px`
    this.spotlightTarget.style.top = `${spotlightTop}px`
    this.spotlightTarget.style.width = `${spotlightWidth}px`
    this.spotlightTarget.style.height = `${spotlightHeight}px`

    const mobile = window.matchMedia("(max-width: 767px)").matches
    if (mobile) {
      this.cardTarget.style.left = "50%"
      this.cardTarget.style.top = "auto"
      this.cardTarget.style.bottom = "1rem"
      this.cardTarget.style.transform = "translateX(-50%)"
      return
    }

    const cardMargin = 16
    const cardRect = this.cardTarget.getBoundingClientRect()
    let left = rect.right + cardMargin
    let top = rect.top

    if (left + cardRect.width > viewportWidth - 12) {
      left = rect.left - cardRect.width - cardMargin
    }

    if (left < 12) {
      left = Math.min(
        Math.max((viewportWidth - cardRect.width) / 2, 12),
        viewportWidth - cardRect.width - 12
      )
    }

    if (top + cardRect.height > viewportHeight - 12) {
      top = Math.max(viewportHeight - cardRect.height - 12, 12)
    }
    if (top < 12) {
      top = 12
    }

    if (!Number.isFinite(left) || !Number.isFinite(top)) {
      this.cardTarget.style.left = "50%"
      this.cardTarget.style.top = "50%"
      this.cardTarget.style.bottom = "auto"
      this.cardTarget.style.transform = "translate(-50%, -50%)"
      return
    }

    this.cardTarget.style.left = `${left}px`
    this.cardTarget.style.top = `${top}px`
    this.cardTarget.style.bottom = "auto"
    this.cardTarget.style.transform = "none"
  }

  scheduleReposition() {
    if (this.rafId) window.cancelAnimationFrame(this.rafId)
    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null
      this.reposition()
    })
  }

  scrollTargetIntoView() {
    if (!this.activeTarget || !this.isElementVisible(this.activeTarget)) return
    this.activeTarget.scrollIntoView({ block: "center", behavior: "auto" })
  }

  shouldShow() {
    if (!Array.isArray(this.stepsValue) || this.stepsValue.length === 0) return false

    if (this.userSignedInValue) {
      return !(this.userSeenValue || this.userAlreadySeenLocally())
    }

    return !this.visitorAlreadySeen()
  }

  visitorAlreadySeen() {
    if (!this.hasVisitorStorageKeyValue) return false

    try {
      return localStorage.getItem(this.visitorStorageKeyValue) === "true"
    } catch (_error) {
      return false
    }
  }

  userAlreadySeenLocally() {
    const key = this.userStorageKey()
    if (!key) return false

    try {
      return localStorage.getItem(key) === "true"
    } catch (_error) {
      return false
    }
  }

  persistSeen() {
    if (!this.userSignedInValue && this.hasVisitorStorageKeyValue) {
      try {
        localStorage.setItem(this.visitorStorageKeyValue, "true")
      } catch (_error) {
        // Ignore storage errors in private mode.
      }
    }

    if (this.userSignedInValue && this.hasMarkSeenUrlValue) {
      const userKey = this.userStorageKey()
      if (userKey) {
        try {
          localStorage.setItem(userKey, "true")
        } catch (_error) {
          // Ignore storage errors in private mode.
        }
      }

      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      fetch(this.markSeenUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ plan_type: this.currentPlanType() }),
        credentials: "same-origin"
      })
    }
  }

  currentPlanType() {
    return this.hasPlanTypeValue ? this.planTypeValue : "fixed_date"
  }

  resolveTarget(selector) {
    if (!selector) return null
    return document.querySelector(selector)
  }

  userStorageKey() {
    if (!this.userSignedInValue || !this.hasPlanTypeValue) return null
    const userId = this.hasUserIdValue && this.userIdValue ? this.userIdValue : "me"
    return `planOnboardingSeenUser:v1:${userId}:${this.planTypeValue}`
  }

  isElementVisible(element) {
    if (!element) return false
    const rect = element.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  cleanup() {
    this.overlayTarget.classList.add("hidden")
    this.spotlightTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  get steps() {
    return this.stepsValue || []
  }
}
